import type { AdminConfigItemDto, JsonValue } from '@carcheck/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { editorSeed } from './configDisplay';
import { parseJsonValue, type SetConfigOutcome } from './useSetConfig';

/**
 * Inline-edit state machine for a single config row (AUT-131). Extracted
 * from `ConfigRow` so the row's render stays under the function-line cap
 * and the type-coercion + parse-validity logic is unit-testable in
 * isolation.
 *
 * Owns: editing flag, the draft string (number/string/json), the draft
 * boolean (toggle), and the inline error. `save` coerces the draft to a
 * `JsonValue` per the wire `type` (architect guardrail #1 — json is
 * `JSON.parse`d on save; parse failure blocks the PATCH + pins "Невалидный
 * JSON"), then delegates to the page's PROACTIVE step-up `onSave` and maps
 * a 400 outcome onto the inline error (architect guardrail #4).
 */
export interface UseConfigRowEditor {
  editing: boolean;
  draft: string;
  setDraft: (next: string) => void;
  boolDraft: boolean;
  setBoolDraft: (next: boolean) => void;
  error: string | undefined;
  begin: () => void;
  cancel: () => void;
  save: () => Promise<void>;
}

export const useConfigRowEditor = (
  item: AdminConfigItemDto,
  onSave: (variables: { key: string; value: JsonValue }) => Promise<SetConfigOutcome>,
): UseConfigRowEditor => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [boolDraft, setBoolDraft] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const begin = (): void => {
    setDraft(editorSeed(item));
    setBoolDraft(item.type === 'boolean' ? item.value === true : false);
    setError(undefined);
    setEditing(true);
  };

  const cancel = (): void => {
    setEditing(false);
    setError(undefined);
  };

  /** Coerce the draft to a `JsonValue`, or set an inline error + return null. */
  const coerce = (): { value: JsonValue } | null => {
    if (item.type === 'boolean') return { value: boolDraft };
    if (item.type === 'number') {
      const n = Number(draft);
      if (draft.trim() === '' || Number.isNaN(n)) {
        setError(t('config.editor.invalidNumber'));
        return null;
      }
      return { value: n };
    }
    if (item.type === 'json') {
      const parsed = parseJsonValue(draft); // 🟠 #1 — parse-validity only.
      if (!parsed.ok) {
        setError(t('config.editor.invalidJson'));
        return null;
      }
      return { value: parsed.value };
    }
    return { value: draft }; // string
  };

  const save = async (): Promise<void> => {
    setError(undefined);
    const coerced = coerce();
    if (!coerced) return;
    const outcome = await onSave({ key: item.key, value: coerced.value });
    if (outcome.ok) {
      setEditing(false);
      return;
    }
    // 🟠 #4 — backend 400 message pinned inline; cancel/generic = no change.
    if (outcome.fieldError) setError(outcome.fieldError);
  };

  return { editing, draft, setDraft, boolDraft, setBoolDraft, error, begin, cancel, save };
};
